import { useState } from 'react';

import {
	PREORDER_STATUS_DISPLAY_LABEL,
	PreorderExternalStatus,
	PREORDERS_TABLE_TAB,
} from '@ekhodealer/ekho-common';
import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Button,
	Group,
	MoreButton,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { PreordersTableRow, PreordersTableTab } from '@ekhodealer/ekho-common';

import {
	ArrowLeft24,
	ArrowRight24,
	ChevronDown12,
	CircleSlash24,
	Info24,
	Link24,
	SquareUp24,
} from '@common/components/icons';

import { useExportPreorders } from '../api/useExportPreorders';
import { useInvitePreorderToCheckout } from '../api/useInvitePreorderToCheckout';
import { useUpdatePreorderStatus } from '../api/useUpdatePreorderStatus';
import { PREORDER_DETAILS_TAB } from '../atoms/preorderDetailsDrawerAtoms';
import { usePreorderNavigation } from '../hooks/usePreorderNavigation';

import { CancelRefundPreorderDialog } from './CancelRefundPreorderDialog';

export const PREORDER_ACTIONS_CONTEXT = {
	TABLE_ROW: 'table-row',
	DRAWER: 'drawer',
} as const;

export type PreorderActionsContext = (typeof PREORDER_ACTIONS_CONTEXT)[keyof typeof PREORDER_ACTIONS_CONTEXT];

interface PreorderActionsProps {
	item: PreordersTableRow;
	currentTab: PreordersTableTab | null;
	context: PreorderActionsContext;
}

export const PreorderActions = ({ item, currentTab, context }: PreorderActionsProps): React.ReactNode => {
	const { openPreorderDetails } = usePreorderNavigation();
	const exportPreordersMutation = useExportPreorders();
	const updateStatusMutation = useUpdatePreorderStatus();
	const inviteMutation = useInvitePreorderToCheckout();
	const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);

	const isTableRow = context === PREORDER_ACTIONS_CONTEXT.TABLE_ROW;
	const isPendingInvitation = currentTab === PREORDERS_TABLE_TAB.PENDING_INVITATION;
	const isAlreadyInvited =
		item.status !== PREORDER_STATUS_DISPLAY_LABEL[PreorderExternalStatus.PENDING_INVITATION];
	const shouldDisableInvite = inviteMutation.isSuccess || isAlreadyInvited;

	const handleViewPreorderDetails = (): void => {
		openPreorderDetails(item.preorderId, PREORDER_DETAILS_TAB.DETAILS);
	};

	const handleExportAsCsv = async (): Promise<void> => {
		try {
			await exportPreordersMutation.mutateAsync({
				preorderIds: [item.preorderId],
			});
			toast({ message: 'Preorder exported', variant: 'success' });
		} catch {
			toast({ message: 'Failed to export preorder. Please try again.', variant: 'error' });
		}
	};

	const buttonSize = isTableRow ? 'small' : 'large';

	const actionMenu = (
		<ActionMenuTrigger>
			{isTableRow ? (
				<MoreButton
					size='medium'
					aria-label={`More actions for preorder ${item.preorderId}`}
					isLoading={updateStatusMutation.isPending}
					isDisabled={updateStatusMutation.isPending || updateStatusMutation.isSuccess}
				/>
			) : (
				<Button
					variant='secondary'
					size='large'
					icon={<ChevronDown12 />}
					iconSide='right'>
					More actions
				</Button>
			)}
			<ActionMenu>
				{isTableRow && (
					<ActionMenuItem
						label='View preorder details'
						leadingAccessory={<Info24 />}
						onAction={handleViewPreorderDetails}
					/>
				)}
				{currentTab === PREORDERS_TABLE_TAB.AWAITING_CHECKOUT && (
					<ActionMenuItem
						label='Mark as idle'
						leadingAccessory={<ArrowRight24 />}
						onAction={() => {
							updateStatusMutation.mutate({
								preorderId: item.preorderId,
								status: PreorderExternalStatus.IDLE,
							});
						}}
					/>
				)}
				{currentTab === PREORDERS_TABLE_TAB.IDLE && (
					<ActionMenuItem
						label='Mark as active'
						leadingAccessory={<ArrowLeft24 />}
						onAction={() => {
							updateStatusMutation.mutate({
								preorderId: item.preorderId,
								status: PreorderExternalStatus.AWAITING_CHECKOUT,
							});
						}}
					/>
				)}
				{item.convertedCheckoutCartId && (
					<ActionMenuItem
						label='Copy checkout cart ID'
						leadingAccessory={<Link24 />}
						onAction={() => {
							navigator.clipboard.writeText(item.convertedCheckoutCartId!);
							toast({
								message: 'Checkout cart ID copied to clipboard',
								variant: 'success',
							});
						}}
					/>
				)}
				<ActionMenuItem
					label='Export as CSV'
					leadingAccessory={<SquareUp24 />}
					onAction={handleExportAsCsv}
				/>
				{item.status !== PREORDER_STATUS_DISPLAY_LABEL[PreorderExternalStatus.CANCELED] &&
					item.status !== PREORDER_STATUS_DISPLAY_LABEL[PreorderExternalStatus.CONVERTED] && (
					<ActionMenuItem
						label='Cancel & refund'
						leadingAccessory={<CircleSlash24 />}
						destructive
						onAction={() => setIsRefundDialogOpen(true)}
					/>
				)}
			</ActionMenu>
		</ActionMenuTrigger>
	);

	const inviteButton = isPendingInvitation ? (
		<Button
			variant='filled'
			size={buttonSize}
			isLoading={inviteMutation.isPending}
			isDisabled={shouldDisableInvite}
			onPress={() =>
				inviteMutation.mutate({
					preorderId: item.preorderId,
					buyerFullName: item.buyerFullName,
				})
			}>
			{shouldDisableInvite ? 'Invited to checkout' : 'Invite to checkout'}
		</Button>
	) : null;

	return (
		<>
			<Group itemsSpacing='8' itemsAlignY='center' noWrap>
				{isTableRow ? (
					<>
						{inviteButton}
						{actionMenu}
					</>
				) : (
					<>
						{actionMenu}
						{inviteButton}
					</>
				)}
			</Group>
			<CancelRefundPreorderDialog
				preorderId={item.preorderId}
				buyerFullName={item.buyerFullName}
				isOpen={isRefundDialogOpen}
				onOpenChange={setIsRefundDialogOpen}
			/>
		</>
	);
};
